import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * Timer used to delay submenu deactivation.
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #deactivateTimeout = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(() => this.#preloadImages());
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    clearTimeout(this.#deactivateTimeout);
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  /**
   * Delay deactivation slightly to prevent the submenu
   * from closing while moving the pointer into it.
   */
  #overflowSubmenuListener = () => {
    clearTimeout(this.#deactivateTimeout);

    this.#deactivateTimeout = setTimeout(() => {
      if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

      this.#deactivate();
    }, 140);
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (
      this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]')
    );
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return (
      this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ??
      false
    );
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    // Cancel a pending deactivation whenever another menu item is activated.
    clearTimeout(this.#deactivateTimeout);

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Make sure all lazy-loaded images inside the mega menu start loading
      this.#preloadImages(submenu);

      // Watch images already present in the submenu
      this.#watchSubmenuImages(submenu);

      // Cleanup any previous observer
      this.#cleanupMutationObserver();

      // Watch for content injected later by Shopify/Horizon
      this.#submenuMutationObserver = new MutationObserver(() => {
        // Make sure newly injected lazy images also start loading
        this.#preloadImages(submenu);

        // Watch newly injected images
        this.#watchSubmenuImages(submenu);

        // Recalculate the submenu height
        this.#updateSubmenuHeight(submenu);
      });

      this.#submenuMutationObserver.observe(submenu, {
        childList: true,
        subtree: true,
      });

      // Give the browser time to finish rendering/hydrating
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.#updateSubmenuHeight(submenu);
        });
      });
    }

    // Use scrollHeight so content that is not currently fully visible
    // is still included in the calculated submenu height.
    let finalHeight = submenu?.scrollHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu
    // or the total height of the menu list links.
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();

      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight
         * is not valid due to the lack of padding. Measuring the overflow menu
         * itself is more reliable.
         */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after a short delay.
   * This prevents the mega menu from closing when the pointer
   * briefly leaves the top-level item while moving into the submenu.
   *
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const relatedTarget = event.relatedTarget;

    const isMovingWithinMenu =
      relatedTarget instanceof Node &&
      (this.contains(relatedTarget) || menu?.contains(relatedTarget));

    const isMovingToOverflowMenu =
      relatedTarget instanceof Node &&
      relatedTarget.closest?.('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu) return;

    clearTimeout(this.#deactivateTimeout);

    this.#deactivateTimeout = setTimeout(() => {
      // Check again before closing. The pointer may have entered
      // the submenu during the delay.
      const activeSubmenu = findSubmenu(this.#state.activeItem);

      const submenuHovered = activeSubmenu?.matches(':hover');
      const menuHovered = this.matches(':hover');

      if (
        submenuHovered ||
        menuHovered ||
        this.overflowListHovered ||
        this.overflowMenu?.matches(':hover')
      ) {
        return;
      }

      this.#deactivate();
    }, 140);
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    clearTimeout(this.#deactivateTimeout);

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();

    if (!slottedMenuLinks) {
      return this.overflowMenu?.offsetHeight || 0;
    }

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (
          link.querySelector('[ref="submenu[]"]')
        );

        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });

    const height = this.overflowMenu?.offsetHeight || 0;

    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });

    return height;
  }

  /**
   * Calculate and set the full open header height.
   * If the submenu is not open, the full open header height is 0.
   *
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute(
      'data-submenu-overlap-bottom-row'
    );

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (
            this.headerComponent.querySelector('.header__row--top')
          )?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;

    const fullOpenHeaderHeight = nothingToOpen
      ? 0
      : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty(
      '--full-open-header-height',
      `${fullOpenHeaderHeight}px`
    );
  }

  /**
   * Preload images that are set to load lazily.
   *
   * @param {HTMLElement} root
   */
  #preloadImages = (root = this) => {
    const images = root.querySelectorAll('img[loading="lazy"]');

    images.forEach((image) => {
      image.removeAttribute('loading');
    });
  };

  /**
   * Watch submenu images and update the submenu height
   * when images finish loading.
   *
   * @param {HTMLElement} submenu
   */
  #watchSubmenuImages = (submenu) => {
    const images = submenu.querySelectorAll('img');

    images.forEach((image) => {
      if (image.dataset.headerMenuWatched === 'true') return;

      image.dataset.headerMenuWatched = 'true';

      if (image.complete) {
        this.#updateSubmenuHeight(submenu);
        return;
      }

      image.addEventListener(
        'load',
        () => {
          this.#updateSubmenuHeight(submenu);
        },
        { once: true }
      );

      image.addEventListener(
        'error',
        () => {
          this.#updateSubmenuHeight(submenu);
        },
        { once: true }
      );
    });
  };

  /**
   * Recalculate the submenu height after the browser
   * has finished rendering or content has changed.
   *
   * @param {HTMLElement} submenu
   */
  #updateSubmenuHeight = (submenu) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!submenu || !this.headerComponent) return;

        const height = submenu.scrollHeight;

        if (height > 0) {
          this.headerComponent.style.setProperty('--submenu-height', `${height}px`);
          this.#setFullOpenHeaderHeight(height);
        }
      });
    });
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"]')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');

  return submenu instanceof HTMLElement ? submenu : null;
}